import CourseCard from '#/components/common/CourseCard'
import Pagination from '#/components/common/Pagination'
import { useCourses } from '../hooks/useCourses'
import NoData from '#/components/common/NoData'
import courseCardDefault from '#/assets/courses/course-1.png'
import { ScrollReveal, RevealItem } from '#/components/common/ScrollReveal'

interface CoursesListProps {
  page?: number
  categorySlug?: string
  selectedTag?: string
  searchQuery?: string
  onPageChange?: (pageZeroIndexed: number) => void
}

export default function CoursesList({
  page = 1,
  categorySlug,
  selectedTag,
  searchQuery,
  onPageChange,
}: CoursesListProps) {
  const { data: coursesRes, isLoading } = useCourses({
    page,
    limit: 6,
    categorySlug,
    tags: selectedTag,
    search: searchQuery ? searchQuery.trim() : undefined,
  })

  const courses = coursesRes?.data || []
  const totalPages = coursesRes?.meta?.totalPages || 1
  const currentPageZeroIndexed = page > 0 ? page - 1 : 0

  return (
    <div className="flex w-full flex-col gap-8">
      {isLoading ? (
        <div className="grid w-full grid-cols-1 gap-7.5 md:grid-cols-2">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="h-[380px] w-full animate-pulse rounded-xl bg-neutral-200/50"
            />
          ))}
        </div>
      ) : courses.length === 0 ? (
        <NoData
          title="No Courses Found"
          description="Try adjusting your category, tag, or search criteria."
        />
      ) : (
        <ScrollReveal
          key={`courses-page-${page}-${categorySlug || 'all'}-${selectedTag || 'all'}-${searchQuery || ''}`}
          stagger={0.08}
          className="grid w-full grid-cols-1 gap-7.5 md:grid-cols-2"
        >
          {courses.map((course) => (
            <RevealItem
              key={course.id}
              direction="down"
              offset={16}
              scale={0.99}
            >
              <CourseCard
                id={course.slug}
                slug={course.slug}
                image={course.image || courseCardDefault}
                tag={course.category?.name}
                title={course.title}
                description={
                  course.shortDescription || course.curriculumDescription || ''
                }
                className="h-full"
              />
            </RevealItem>
          ))}
        </ScrollReveal>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            pageCount={totalPages}
            currentPage={currentPageZeroIndexed}
            onPageChange={(p) => onPageChange?.(p)}
          />
        </div>
      )}
    </div>
  )
}
